import { CarrierServicingGrantInputDto, EffectiveServicingDto } from '@groundfloor-platform/mgmt-service-g-sdk';

export interface ServicingChange {
  grants: CarrierServicingGrantInputDto[];
  revokes: CarrierServicingGrantInputDto[];
}

/**
 * What this group holds directly. `listServicing` also reports what it inherits from a container
 * above it, and those are not ours to revoke — only the group that holds a grant can give it up.
 */
export function toDirectServicing(effective: EffectiveServicingDto[]): CarrierServicingGrantInputDto[] {
  return effective
    .filter((grant) => !grant.inheritedFrom)
    .map((grant) => ({ projectID: grant.projectID, permissions: grant.permissions }));
}

/**
 * The calls needed to get from one set of grants to another.
 *
 * Granting is additive and idempotent per permission — it never replaces a set — so a changed
 * network needs both halves: grant what was added, revoke what was removed. A network left exactly
 * as it was produces no call at all.
 */
export function diffServicing(
  current: CarrierServicingGrantInputDto[],
  desired: CarrierServicingGrantInputDto[]
): ServicingChange {
  const projectIDs = new Set([...current, ...desired].map((grant) => grant.projectID));
  const permissionsFor = (grants: CarrierServicingGrantInputDto[], projectID: string) =>
    grants.find((grant) => grant.projectID === projectID)?.permissions ?? [];

  const grants: CarrierServicingGrantInputDto[] = [];
  const revokes: CarrierServicingGrantInputDto[] = [];

  projectIDs.forEach((projectID) => {
    const before = permissionsFor(current, projectID);
    const after = permissionsFor(desired, projectID);

    const added = after.filter((permission) => !before.includes(permission));
    const removed = before.filter((permission) => !after.includes(permission));

    if (added.length) {
      grants.push({ projectID, permissions: added });
    }
    if (removed.length) {
      revokes.push({ projectID, permissions: removed });
    }
  });

  return { grants, revokes };
}

export function hasServicingChanges(change: ServicingChange): boolean {
  return change.grants.length > 0 || change.revokes.length > 0;
}
